// Motion diff (presentation-2 contract section 8.1). Pure: compares two table models by object
// ID and zone counts and lists what changed. Hidden objects carry opaque IDs, so their movement
// is described only by zone count changes, never by identity.

import type { TableModel } from '../model/table.ts';
import type { Scalar } from '../protocol/ui.ts';

export interface Place { zone: string; index: number; at: string | null; ref: string | null; stack: string | null }

export type MotionEvent =
  | { type: 'move'; id: string; from: Place; to: Place }
  | { type: 'enter'; id: string; zone: string; source: { zone: string } | { seat: string } | null }
  | { type: 'exit'; id: string; zone: string; dest: { zone: string } | { seat: string } | null }
  | { type: 'count'; from: string | null; to: string | null; amount: number }
  | { type: 'counter'; id: string; from: Scalar; to: Scalar; delta: number | null }
  | { type: 'owner'; id: string; from: string | null; to: string | null }
  | { type: 'appearance'; id: string }
  | { type: 'turn'; from: string | null; to: string | null };

type Table = Pick<TableModel, 'zones' | 'objects' | 'counters' | 'actor'>;

const PILE_KINDS = new Set(['stack', 'discard', 'supply']);

function places(table: Table): Map<string, Place> {
  const out = new Map<string, Place>();
  for (const zone of table.zones) zone.objects.forEach((o, index) => {
    if (!out.has(o.id)) out.set(o.id, { zone: zone.id, index, at: o.at, ref: o.ref, stack: o.stack });
  });
  for (const o of table.objects.values()) if (!out.has(o.id)) out.set(o.id, { zone: o.zoneId, index: -1, at: o.at, ref: o.ref, stack: o.stack });
  return out;
}

/** Pieces in a zone: its declared count, else its listed objects, each counting `count` pieces. */
function zoneCounts(table: Table): Map<string, number> {
  const out = new Map<string, number>();
  for (const z of table.zones) out.set(z.id, z.count ?? z.objects.reduce((n, o) => n + (o.count ?? 1), 0));
  return out;
}

/** Lists the changes from `prev` to `next`. Order: moves, exits, enters, counts, owners, counters, turn. */
export function diff(prev: Table, next: Table): MotionEvent[] {
  const events: MotionEvent[] = [];
  const before = places(prev), after = places(next);
  const counts0 = zoneCounts(prev), counts1 = zoneCounts(next);
  const delta = new Map<string, number>();
  for (const id of new Set([...counts0.keys(), ...counts1.keys()])) {
    const d = (counts1.get(id) ?? 0) - (counts0.get(id) ?? 0);
    if (d !== 0) delta.set(id, d);
  }
  const kindOf = new Map([...prev.zones, ...next.zones].map(z => [z.id, z.kind]));
  const actor = prev.actor;
  // Each pile unit explains at most one arrival or departure.
  const take = (sign: 1 | -1, exclude: string, preferPile: boolean): string | null => {
    const candidates = [...delta].filter(([id, d]) => id !== exclude && Math.sign(d) === sign);
    const pick = candidates.find(([id]) => !preferPile || PILE_KINDS.has(kindOf.get(id) ?? '')) ?? (preferPile ? undefined : candidates[0]);
    if (!pick) return null;
    delta.set(pick[0], pick[1] - sign);
    if (delta.get(pick[0]) === 0) delta.delete(pick[0]);
    return pick[0];
  };
  const settle = (zone: string, amount: number) => {
    const d = (delta.get(zone) ?? 0) - amount;
    if (d === 0) delta.delete(zone); else delta.set(zone, d);
  };

  for (const [id, to] of after) {
    const from = before.get(id);
    if (from && (from.zone !== to.zone || from.index !== to.index || from.at !== to.at || from.ref !== to.ref || from.stack !== to.stack)) {
      events.push({ type: 'move', id, from, to });
      if (from.zone !== to.zone) {
        const o = next.objects.get(id), n = o?.count ?? 1;
        settle(from.zone, -n); settle(to.zone, n);
      }
    }
  }
  const exits: MotionEvent[] = [], enters: MotionEvent[] = [];
  for (const [id, from] of before) {
    if (after.has(id)) continue;
    settle(from.zone, -(prev.objects.get(id)?.count ?? 1));
    const zone = take(1, from.zone, true) ?? take(1, from.zone, false);
    exits.push({ type: 'exit', id, zone: from.zone, dest: zone ? { zone } : actor ? { seat: actor } : null });
  }
  for (const [id, to] of after) {
    if (before.has(id)) continue;
    settle(to.zone, next.objects.get(id)?.count ?? 1);
    const zone = take(-1, to.zone, true);
    enters.push({ type: 'enter', id, zone: to.zone, source: zone ? { zone } : actor ? { seat: actor } : null });
  }
  events.push(...exits, ...enters);

  // Remaining count changes (hidden cards, aggregated stacks) pair decreases with increases.
  const losses = [...delta].filter(([, d]) => d < 0).map(([id, d]) => ({ id, n: -d }));
  const gains = [...delta].filter(([, d]) => d > 0).map(([id, d]) => ({ id, n: d }));
  for (const gain of gains) {
    while (gain.n > 0) {
      const loss = losses.find(l => l.n > 0);
      const amount = loss ? Math.min(loss.n, gain.n) : gain.n;
      events.push({ type: 'count', from: loss?.id ?? null, to: gain.id, amount });
      gain.n -= amount;
      if (loss) loss.n -= amount;
    }
  }
  for (const loss of losses) if (loss.n > 0) events.push({ type: 'count', from: loss.id, to: null, amount: loss.n });

  for (const [id, o] of next.objects) {
    const p = prev.objects.get(id);
    if (p && p.owner !== o.owner) events.push({ type: 'owner', id, from: p.owner, to: o.owner });
    if (p && (p.catalogKey !== o.catalogKey || p.count !== o.count || p.faceDown !== o.faceDown || JSON.stringify(p.fields) !== JSON.stringify(o.fields) || JSON.stringify(p.tags) !== JSON.stringify(o.tags))) events.push({ type: 'appearance', id });
  }
  const counters0 = new Map(prev.counters.map(c => [c.id, c.value]));
  for (const c of next.counters) {
    const old = counters0.get(c.id);
    if (old === undefined || old === c.value) continue;
    const d = typeof old === 'number' && typeof c.value === 'number' ? c.value - old : null;
    events.push({ type: 'counter', id: c.id, from: old, to: c.value, delta: d });
  }
  if (prev.actor !== next.actor) events.push({ type: 'turn', from: prev.actor, to: next.actor });
  return events;
}
